// src/components/layout/AffiliateLayout.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAffiliate } from '../../context/AffiliateContext';
import { getBaseUrl } from '../../config/api';
import Logo from '../common/Logo';
import '../../styles/AffiliateLayout.css';

const AffiliateLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setAffiliateStatus, isPending } = useAffiliate();
  const [drawerOpen, setDrawerOpen] = useState(() => window.innerWidth >= 960);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 960);
  const [pendingBannerDismissed, setPendingBannerDismissed] = useState(false);

  // Load affiliate status from profile on mount (profile is allowed for pending)
  const loadAffiliateStatus = useCallback(async () => {
    const token = localStorage.getItem('affiliateToken') || sessionStorage.getItem('affiliateToken');
    if (!token) return;
    try {
      const res = await fetch(`${getBaseUrl()}/v1/affiliate/profile`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (res.ok && data.success && data.data?.status) {
        setAffiliateStatus(data.data.status as 'pending' | 'active' | 'suspended' | 'rejected');
      }
      if (res.status === 401) {
        setAffiliateStatus(null);
        localStorage.removeItem('affiliateToken');
        sessionStorage.removeItem('affiliateToken');
        localStorage.removeItem('affiliateUser');
        sessionStorage.removeItem('affiliateUser');
        navigate('/affiliate/login', { replace: true });
      }
    } catch (err) {
      console.error('[AffiliateLayout] Failed to load profile for status:', err);
    }
  }, [navigate, setAffiliateStatus]);

  useEffect(() => {
    loadAffiliateStatus();
  }, [loadAffiliateStatus]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 960;
      setIsMobile(mobile);
      if (!mobile) {
        setDrawerOpen(true);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = () => {
    setAffiliateStatus(null);
    localStorage.removeItem('affiliateToken');
    sessionStorage.removeItem('affiliateToken');
    localStorage.removeItem('affiliateUser');
    sessionStorage.removeItem('affiliateUser');
    localStorage.removeItem('affiliateRefreshToken');
    sessionStorage.removeItem('affiliateRefreshToken');
    navigate('/affiliate/login');
  };

  const navigationItems = [
    { path: '/affiliate/dashboard', label: 'Dashboard', icon: '📊', restricted: false },
    { path: '/affiliate/commissions', label: 'Commissions', icon: '💰', restricted: true },
    { path: '/affiliate/payouts', label: 'Payouts', icon: '💳', restricted: true },
    { path: '/affiliate/marketing-materials', label: 'Marketing Materials', icon: '📱', restricted: true },
    { path: '/affiliate/analytics', label: 'Analytics', icon: '📈', restricted: false },
    { path: '/affiliate/profile', label: 'Profile', icon: '👤', restricted: false },
    { path: '/affiliate/help', label: 'Help & Support', icon: '❓', restricted: false },
  ];

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const showPendingBanner = isPending && !pendingBannerDismissed;

  return (
    <div className="affiliate-layout">
      {/* Sidebar */}
      <aside className={`affiliate-sidebar ${drawerOpen ? 'open' : ''}`}>
        <div className="affiliate-sidebar-header">
          <Link to="/affiliate/dashboard" className="affiliate-logo-link">
            <Logo size="small" />
          </Link>
          {!isMobile && (
            <button
              className="sidebar-toggle"
              onClick={() => setDrawerOpen(!drawerOpen)}
              aria-label="Toggle sidebar"
            >
              {drawerOpen ? '←' : '→'}
            </button>
          )}
        </div>

        <nav className="affiliate-nav">
          <ul className="affiliate-nav-list">
            {navigationItems.map((item) => {
              const locked = item.restricted && isPending;
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={`affiliate-nav-item ${isActive(item.path) ? 'active' : ''} ${locked ? 'affiliate-nav-item--locked' : ''}`}
                    title={locked ? 'Available after approval' : undefined}
                    aria-disabled={locked ? true : undefined}
                  >
                    <span className="nav-icon">{item.icon}</span>
                    {drawerOpen && (
                      <>
                        <span className="nav-label">{item.label}</span>
                        {locked && <span className="nav-locked-badge" aria-label="Pending approval">🔒</span>}
                      </>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="affiliate-sidebar-footer">
          {drawerOpen && (
            <Link to="/affiliate-terms" target="_blank" className="affiliate-sidebar-legal-link">
              Referral Agent Program Terms
            </Link>
          )}
          <button onClick={handleLogout} className="affiliate-logout-btn">
            <span className="nav-icon">🚪</span>
            {drawerOpen && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="affiliate-main">
        {/* Pending approval banner */}
        {showPendingBanner && (
          <div className="affiliate-pending-banner" role="status">
            <div className="affiliate-pending-banner__content">
              <span className="affiliate-pending-banner__icon" aria-hidden>⏳</span>
              <p className="affiliate-pending-banner__text">
                Your application is under review. You’ll get access to referral links, commissions, and payouts once approved. You can still use <Link to="/affiliate/profile">Profile</Link> and <Link to="/affiliate/help">Help</Link>.
              </p>
              <button
                type="button"
                className="affiliate-pending-banner__dismiss"
                onClick={() => setPendingBannerDismissed(true)}
                aria-label="Dismiss banner"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Mobile Header */}
        {isMobile && (
          <header className="affiliate-mobile-header">
            <button
              className="mobile-menu-toggle"
              onClick={() => setDrawerOpen(!drawerOpen)}
              aria-label="Toggle menu"
            >
              ☰
            </button>
            <Link to="/affiliate/dashboard">
              <Logo size="small" />
            </Link>
            <div style={{ width: '40px' }}></div>
          </header>
        )}

        {/* Mobile Overlay */}
        {isMobile && drawerOpen && (
          <div
            className="affiliate-overlay"
            onClick={() => setDrawerOpen(false)}
          />
        )}

        {/* Page Content */}
        <div className="affiliate-content">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AffiliateLayout;

